export const DOCUMENT_SESSION_PROTOCOL_VERSION = 1 as const

export type DocumentSessionDocScope =
  | 'workbook_root'
  | 'sheet_chunk'
  | 'presence'

export type DocumentSessionCheckpoint = {
  workbook_id: string
  session_id: string
  snapshot_seq: number
  last_op_seq: number
}

export type DocumentSessionLease = {
  session_id: string
  resume_token: string
  expires_at_epoch_ms: number
}

export type SheetChunkKey = {
  sheet_id: string
  row_block: number
  col_block: number
}

export const SPREADSHEET_ENGINE_SHEET_CHUNK_ROWS = 128 as const
export const SPREADSHEET_ENGINE_SHEET_CHUNK_COLS = 64 as const

export type SheetChunkDescriptor = {
  key: SheetChunkKey
  snapshot_seq: number
  op_seq: number
  bytes_hint?: number | null
}

export type DocumentProjectionDescriptor = {
  kind: string
  version: number
  uri?: string | null
  applied_op_seq: number
  metadata?: unknown
}

export type DocumentSessionCommittedUpdate = {
  op_seq: number
  doc_scope: DocumentSessionDocScope
  chunk_id?: string | null
  update_payload_b64: string
  actor_user_id: string
  client_id?: string | null
  client_update_id?: string | null
}

export type DocumentSessionBootstrap = {
  protocol_version: typeof DOCUMENT_SESSION_PROTOCOL_VERSION
  workbook_id: string
  session_id: string
  snapshot_seq: number
  snapshot_payload_b64?: string | null
  chunk_snapshot_payloads_b64?: Record<string, string>
  tail: DocumentSessionCommittedUpdate[]
  chunk_descriptors: SheetChunkDescriptor[]
  projection_descriptors: DocumentProjectionDescriptor[]
  lease: DocumentSessionLease
}

export type DocumentSessionClientUpdate = {
  client_id: string
  client_update_id: string
  doc_scope: DocumentSessionDocScope
  chunk_id?: string | null
  update_payload_b64: string
}

export type DocumentSessionClientMessage =
  | {
    type: 'bootstrap'
    protocol_version: typeof DOCUMENT_SESSION_PROTOCOL_VERSION
    workbook_id: string
  }
  | {
    type: 'resume'
    protocol_version: typeof DOCUMENT_SESSION_PROTOCOL_VERSION
    workbook_id: string
    checkpoint: DocumentSessionCheckpoint
    lease: DocumentSessionLease
  }
  | {
    type: 'sync'
    protocol_version: typeof DOCUMENT_SESSION_PROTOCOL_VERSION
    workbook_id: string
    updates: DocumentSessionClientUpdate[]
  }
  | {
    type: 'presence_update'
    protocol_version: typeof DOCUMENT_SESSION_PROTOCOL_VERSION
    workbook_id: string
    payload: unknown
  }

export type DocumentSessionServerMessage =
  | ({
    type: 'bootstrap_ready'
  } & DocumentSessionBootstrap)
  | {
    type: 'resume_ack'
    protocol_version: typeof DOCUMENT_SESSION_PROTOCOL_VERSION
    workbook_id: string
    session_id: string
    snapshot_seq: number
    head_op_seq: number
  }
  | {
    type: 'updates_committed'
    protocol_version: typeof DOCUMENT_SESSION_PROTOCOL_VERSION
    workbook_id: string
    session_id: string
    updates: DocumentSessionCommittedUpdate[]
  }
  | {
    type: 'presence_broadcast'
    protocol_version: typeof DOCUMENT_SESSION_PROTOCOL_VERSION
    workbook_id: string
    payload: unknown
  }
  | {
    type: 'rebootstrap_required'
    protocol_version: typeof DOCUMENT_SESSION_PROTOCOL_VERSION
    workbook_id: string
    reason: string
  }

const normalizeSeq = (value: unknown): number => {
  const next = Number(value)
  if (!Number.isFinite(next) || next < 0) return 0
  return Math.floor(next)
}

const normalizeNonNegativeInteger = (value: unknown): number | null => {
  const next = Number(value)
  if (!Number.isInteger(next) || next < 0) return null
  return next
}

const isDocumentSessionRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))

const normalizeDocumentSessionDocScope = (value: unknown): DocumentSessionDocScope | null =>
  value === 'workbook_root' || value === 'sheet_chunk' || value === 'presence'
    ? value
    : null

const hasSupportedDocumentSessionProtocolVersion = (value: unknown): boolean =>
  Number(value) === DOCUMENT_SESSION_PROTOCOL_VERSION

export const normalizeDocumentSessionSheetChunkId = (value: unknown): string | null => {
  const raw = String(value || '').trim()
  if (!raw) return null
  const parts = raw.split('::')
  if (parts.length !== 3) return null
  const sheetId = String(parts[0] || '').trim()
  const rowBlock = Number(parts[1])
  const colBlock = Number(parts[2])
  if (
    !sheetId ||
    !Number.isInteger(rowBlock) ||
    !Number.isInteger(colBlock) ||
    rowBlock < 0 ||
    colBlock < 0
  ) {
    return null
  }
  return `${sheetId}::${rowBlock}::${colBlock}`
}

const normalizeChunkSnapshotPayloads = (value: unknown): Record<string, string> => {
  if (!isDocumentSessionRecord(value)) return {}
  const entries = Object.entries(value)
    .map(([chunkIdRaw, payloadRaw]): [string, string] => [
      normalizeDocumentSessionSheetChunkId(chunkIdRaw) || '',
      String(payloadRaw || '').trim(),
    ])
    .filter(([chunkId, payload]) => Boolean(chunkId && payload))
  return Object.fromEntries(entries)
}

export const normalizeDocumentSessionLease = (value: unknown): DocumentSessionLease => {
  const record = isDocumentSessionRecord(value) ? value : {}
  return {
    session_id: String(record.session_id || '').trim(),
    resume_token: String(record.resume_token || '').trim(),
    expires_at_epoch_ms: normalizeSeq(record.expires_at_epoch_ms),
  }
}

export const normalizeDocumentSessionCheckpoint = (value: unknown): DocumentSessionCheckpoint => {
  const record = isDocumentSessionRecord(value) ? value : {}
  return buildDocumentSessionCheckpoint(
    String(record.workbook_id || '').trim(),
    String(record.session_id || '').trim(),
    normalizeSeq(record.snapshot_seq),
    normalizeSeq(record.last_op_seq),
  )
}

const normalizeDocumentSessionCommittedUpdate = (value: unknown): DocumentSessionCommittedUpdate | null => {
  if (!isDocumentSessionRecord(value)) return null
  const docScope = normalizeDocumentSessionDocScope(value.doc_scope)
  if (!docScope) return null
  if (docScope === 'presence') return null
  const updatePayload = String(value.update_payload_b64 || '').trim()
  if (!updatePayload) return null
  const update: DocumentSessionCommittedUpdate = {
    op_seq: normalizeSeq(value.op_seq),
    doc_scope: docScope,
    update_payload_b64: updatePayload,
    actor_user_id: String(value.actor_user_id || '').trim(),
  }
  const chunkId = normalizeDocumentSessionSheetChunkId(value.chunk_id)
  if (docScope === 'sheet_chunk') {
    if (!chunkId) return null
    update.chunk_id = chunkId
  }
  const clientId = String(value.client_id || '').trim()
  if (clientId) update.client_id = clientId
  const clientUpdateId = String(value.client_update_id || '').trim()
  if (clientUpdateId) update.client_update_id = clientUpdateId
  return update
}

export const normalizeDocumentSessionClientUpdate = (value: unknown): DocumentSessionClientUpdate | null => {
  if (!isDocumentSessionRecord(value)) return null
  const clientId = String(value.client_id || '').trim()
  const clientUpdateId = String(value.client_update_id || '').trim()
  const updatePayload = String(value.update_payload_b64 || '').trim()
  if (!clientId || !clientUpdateId || !updatePayload) return null
  const docScope = normalizeDocumentSessionDocScope(value.doc_scope)
  if (!docScope) return null
  if (docScope === 'presence') return null
  const update: DocumentSessionClientUpdate = {
    client_id: clientId,
    client_update_id: clientUpdateId,
    doc_scope: docScope,
    update_payload_b64: updatePayload,
  }
  const chunkId = normalizeDocumentSessionSheetChunkId(value.chunk_id)
  if (docScope === 'sheet_chunk') {
    if (!chunkId) return null
    update.chunk_id = chunkId
  }
  return update
}

export const normalizeSheetChunkDescriptor = (value: unknown): SheetChunkDescriptor | null => {
  if (!isDocumentSessionRecord(value) || !isDocumentSessionRecord(value.key)) return null
  const sheetId = String(value.key.sheet_id || '').trim()
  if (!sheetId) return null
  const rowBlock = normalizeNonNegativeInteger(value.key.row_block)
  const colBlock = normalizeNonNegativeInteger(value.key.col_block)
  if (rowBlock == null || colBlock == null) return null
  return {
    key: {
      sheet_id: sheetId,
      row_block: rowBlock,
      col_block: colBlock,
    },
    snapshot_seq: normalizeSeq(value.snapshot_seq),
    op_seq: normalizeSeq(value.op_seq),
    bytes_hint: value.bytes_hint == null ? null : normalizeSeq(value.bytes_hint),
  }
}

export const normalizeProjectionDescriptor = (value: unknown): DocumentProjectionDescriptor | null => {
  if (!isDocumentSessionRecord(value)) return null
  const kind = String(value.kind || '').trim()
  if (!kind) return null
  const normalized: DocumentProjectionDescriptor = {
    kind,
    version: normalizeSeq(value.version) || 1,
    applied_op_seq: normalizeSeq(value.applied_op_seq),
  }
  const uri = String(value.uri || '').trim()
  if (uri) normalized.uri = uri
  if (value.metadata != null) normalized.metadata = value.metadata
  return normalized
}

export const buildDocumentSessionCheckpoint = (
  workbookIdRaw: string,
  sessionIdRaw: string,
  snapshotSeq: number,
  lastOpSeq: number,
): DocumentSessionCheckpoint => ({
  workbook_id: String(workbookIdRaw || '').trim(),
  session_id: String(sessionIdRaw || '').trim(),
  snapshot_seq: normalizeSeq(snapshotSeq),
  last_op_seq: normalizeSeq(lastOpSeq),
})

export const buildDocumentSessionBootstrapMessage = (
  workbookIdRaw: string,
): DocumentSessionClientMessage => ({
  type: 'bootstrap',
  protocol_version: DOCUMENT_SESSION_PROTOCOL_VERSION,
  workbook_id: String(workbookIdRaw || '').trim(),
})

export const buildDocumentSessionResumeMessage = (
  workbookIdRaw: string,
  checkpoint: unknown,
  lease: unknown,
): DocumentSessionClientMessage => ({
  type: 'resume',
  protocol_version: DOCUMENT_SESSION_PROTOCOL_VERSION,
  workbook_id: String(workbookIdRaw || '').trim(),
  checkpoint: normalizeDocumentSessionCheckpoint(checkpoint),
  lease: normalizeDocumentSessionLease(lease),
})

export const buildDocumentSessionSyncMessage = (
  workbookIdRaw: string,
  updates: readonly unknown[],
): DocumentSessionClientMessage => ({
  type: 'sync',
  protocol_version: DOCUMENT_SESSION_PROTOCOL_VERSION,
  workbook_id: String(workbookIdRaw || '').trim(),
  updates: Array.isArray(updates)
    ? updates.map(normalizeDocumentSessionClientUpdate).filter((update): update is DocumentSessionClientUpdate => Boolean(update))
    : [],
})

export const buildSheetChunkKey = (
  sheetIdRaw: string,
  row: number,
  col: number,
  rowBlockSize = SPREADSHEET_ENGINE_SHEET_CHUNK_ROWS,
  colBlockSize = SPREADSHEET_ENGINE_SHEET_CHUNK_COLS,
): SheetChunkKey => ({
  sheet_id: String(sheetIdRaw || '').trim(),
  row_block: Math.max(0, Math.floor(Number(row) || 0) / Math.max(1, rowBlockSize) | 0),
  col_block: Math.max(0, Math.floor(Number(col) || 0) / Math.max(1, colBlockSize) | 0),
})

export const buildSheetChunkId = (key: SheetChunkKey): string => {
  const sheetId = String(key.sheet_id || '').trim()
  const rowBlock = normalizeNonNegativeInteger(key.row_block)
  const colBlock = normalizeNonNegativeInteger(key.col_block)
  if (!sheetId || rowBlock == null || colBlock == null) {
    throw new Error('Invalid document-session sheet chunk key')
  }
  return `${sheetId}::${rowBlock}::${colBlock}`
}

export const normalizeDocumentSessionServerMessage = (
  input: unknown,
): DocumentSessionServerMessage | null => {
  const record = isDocumentSessionRecord(input) ? input : {}
  const projectionDescriptors = Array.isArray(record.projection_descriptors)
    ? record.projection_descriptors.map(normalizeProjectionDescriptor).filter((descriptor): descriptor is DocumentProjectionDescriptor => Boolean(descriptor))
    : []
  const type = String(record.type || '').trim()
  if (!hasSupportedDocumentSessionProtocolVersion(record.protocol_version)) return null
  if (type === 'bootstrap_ready') {
    return {
      type: 'bootstrap_ready',
      protocol_version: DOCUMENT_SESSION_PROTOCOL_VERSION,
      workbook_id: String(record.workbook_id || '').trim(),
      session_id: String(record.session_id || '').trim(),
      snapshot_seq: normalizeSeq(record.snapshot_seq),
      snapshot_payload_b64: record.snapshot_payload_b64 ? String(record.snapshot_payload_b64).trim() : null,
      chunk_snapshot_payloads_b64: normalizeChunkSnapshotPayloads(record.chunk_snapshot_payloads_b64),
      tail: Array.isArray(record.tail)
        ? record.tail.map(normalizeDocumentSessionCommittedUpdate).filter((update): update is DocumentSessionCommittedUpdate => Boolean(update))
        : [],
      chunk_descriptors: Array.isArray(record.chunk_descriptors)
        ? record.chunk_descriptors.map(normalizeSheetChunkDescriptor).filter((descriptor): descriptor is SheetChunkDescriptor => Boolean(descriptor))
        : [],
      projection_descriptors: projectionDescriptors,
      lease: normalizeDocumentSessionLease(record.lease),
    }
  }
  if (type === 'resume_ack') {
    return {
      type: 'resume_ack',
      protocol_version: DOCUMENT_SESSION_PROTOCOL_VERSION,
      workbook_id: String(record.workbook_id || '').trim(),
      session_id: String(record.session_id || '').trim(),
      snapshot_seq: normalizeSeq(record.snapshot_seq),
      head_op_seq: normalizeSeq(record.head_op_seq),
    }
  }
  if (type === 'updates_committed') {
    return {
      type: 'updates_committed',
      protocol_version: DOCUMENT_SESSION_PROTOCOL_VERSION,
      workbook_id: String(record.workbook_id || '').trim(),
      session_id: String(record.session_id || '').trim(),
      updates: Array.isArray(record.updates)
        ? record.updates.map(normalizeDocumentSessionCommittedUpdate).filter((update): update is DocumentSessionCommittedUpdate => Boolean(update))
        : [],
    }
  }
  if (type === 'presence_broadcast') {
    return {
      type: 'presence_broadcast',
      protocol_version: DOCUMENT_SESSION_PROTOCOL_VERSION,
      workbook_id: String(record.workbook_id || '').trim(),
      payload: record.payload ?? null,
    }
  }
  if (type === 'rebootstrap_required') {
    return {
      type: 'rebootstrap_required',
      protocol_version: DOCUMENT_SESSION_PROTOCOL_VERSION,
      workbook_id: String(record.workbook_id || '').trim(),
      reason: String(record.reason || '').trim(),
    }
  }
  return null
}
